import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';
import type { Chamada, StatusPresencaAula } from '../../types/models';
import { CHAMADAS } from '../routes';

/**
 * Hook para Chamadas (registro de presença em aula).
 * GET/POST /escolas/:escolaId/chamadas
 */
function unwrapChamadasList(data: unknown): { items: Chamada[]; total: number } {
  if (Array.isArray(data)) {
    return { items: data as Chamada[], total: data.length };
  }
  const body = (data || {}) as {
    items?: Chamada[];
    data?: Chamada[];
    total?: number;
  };
  const items = body.items ?? body.data ?? [];
  return {
    items: Array.isArray(items) ? items : [],
    total: typeof body.total === 'number' ? body.total : items.length,
  };
}

export function useChamadas(filters?: {
  data?: string;
  dataInicio?: string;
  dataFim?: string;
  turmaId?: string;
  professorId?: string;
  status?: string;
  /** Página (default API = 1). Ignorado se fetchAll. */
  pagina?: number;
  /** Limite por página (API max 200). Default 50; use 200 no dashboard do dia. */
  limite?: number;
  /**
   * Busca todas as páginas até `total`.
   * Necessário no painel (72 turmas × N aulas ultrapassa o default 50).
   */
  fetchAll?: boolean;
  /** false enquanto o relógio simulado ainda não sincronizou. */
  enabled?: boolean;
  refetchIntervalMs?: number | false;
}) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const queryClient = useQueryClient();
  const basePath = `/escolas/${escolaId}/chamadas`;

  const query = useQuery({
    queryKey: ['chamadas', escolaId, filters],
    queryFn: async () => {
      // Backend espera dataInicio/dataFim, não 'data'; resposta = { items, total }.
      const baseParams: Record<string, string | number | undefined> = {
        turmaId: filters?.turmaId,
        professorId: filters?.professorId,
        status: filters?.status,
        limite: filters?.limite ?? (filters?.fetchAll || filters?.data || filters?.dataInicio ? 200 : 50),
      };
      if (filters?.dataInicio) {
        baseParams.dataInicio = filters.dataInicio;
      }
      if (filters?.dataFim) {
        baseParams.dataFim = filters.dataFim;
      }
      if (filters?.data) {
        baseParams.dataInicio = filters.data;
        baseParams.dataFim = filters.data;
      }

      if (!filters?.fetchAll) {
        const { data } = await api.get(basePath, {
          params: { ...baseParams, pagina: filters?.pagina ?? 1 },
        });
        return unwrapChamadasList(data).items;
      }

      const all: Chamada[] = [];
      let pagina = 1;
      let total = Infinity;
      const limite = Number(baseParams.limite) || 200;
      while (all.length < total && pagina <= 50) {
        const { data } = await api.get(basePath, {
          params: { ...baseParams, pagina, limite },
        });
        const page = unwrapChamadasList(data);
        total = page.total;
        all.push(...page.items);
        if (!page.items.length) break;
        pagina += 1;
      }
      return all;
    },
    enabled: !!escolaId && (filters?.enabled ?? true),
    refetchInterval: filters?.refetchIntervalMs ?? false,
  });

  /** Criar chamada nova com lista de alunos */
  const criar = useMutation({
    mutationFn: async (payload: {
      turmaId: string;
      materiaId?: string;
      horarioAulaId?: string;
      data: string;
      alunos: { alunoId: string; status: StatusPresencaAula; observacao?: string }[];
      observacoes?: string;
    }) => {
      try {
        const res = await api.post(basePath, payload);
        return res;
      } catch (error: any) {
        // Se a internet caiu ou a API tá fora
        if (!navigator.onLine || error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          console.warn('[Offline] Salvando chamada na fila local para sincronização futura.', payload);
          const { addChamadaToSyncQueue } = await import('../../lib/offline/syncQueue');
          await addChamadaToSyncQueue(payload);
          return { data: { success: true, offline: true } };
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chamadas'] });
      queryClient.invalidateQueries({ queryKey: ['presencas-diarias'] });
      
      // Dispatch custom event to notify UI that offline queue might have changed
      window.dispatchEvent(new Event('offline-queue-updated'));
    },
  });

  /** Concluir chamada aberta */
  const concluir = useMutation({
    mutationFn: (chamadaId: string) => api.patch(`${basePath}/${chamadaId}/concluir`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chamadas'] }),
  });

  return {
    chamadas: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    criar,
    concluir,
  };
}

/**
 * Hook para buscar status das chamadas do dia para um professor.
 * GET /escolas/:escolaId/chamadas/status-dia?professorId=...&data=...
 */
export function useStatusDia(professorId?: string, data?: string) {
  const escolaId = useAuthStore((s) => s.escolaId);

  const query = useQuery({
    queryKey: ['chamadas-status-dia', escolaId, professorId ?? 'escola', data],
    queryFn: async () => {
      const params: any = { data };
      if (professorId) params.professorId = professorId;
      const { data: result } = await api.get(CHAMADAS.statusDia(escolaId!), { params });
      return (Array.isArray(result) ? result : []) as {
        id: string; turmaId: string; materiaId?: string; horarioAulaId?: string;
        numeroAula?: number | null;
        status: string; totalPresentes: number; totalAusentes: number; totalAtrasados: number; totalAlunos: number;
      }[];
    },
    // Escola/coordenação: sem professorId = visão completa do dia (backend já aceita).
    enabled: !!escolaId && !!data,
    refetchInterval: 30_000, // atualiza a cada 30s
  });

  return {
    chamadasDia: query.data ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
